import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import { toBase, type UnitDef } from "@/lib/tool-kit/physics-fields/units";

/**
 * A typed measurement in its chosen unit, as an SI number — or null when the box is empty
 * or not a number. Scientific notation (`6.674e-11`, `5.97 × 10^24`) is accepted.
 */
export function readQuantity(
  raw: string,
  units: readonly UnitDef[],
  unitId: string
): number | null {
  if (raw.trim() === "") {
    return null;
  }
  const value = parseMeasuredNumber(raw);
  return value === null ? null : toBase(units, unitId, value);
}

/** A plain typed number with no unit (an angle, a ratio), or null. */
export function readPlain(raw: string): number | null {
  return raw.trim() === "" ? null : parseMeasuredNumber(raw);
}

/** A solved value written so it can be typed back into a box: `1.982e20`, never `1.982 × 10²⁰`. */
export function typedValue(value: number, sig: number): string {
  const rounded = Number(value.toPrecision(sig));
  const magnitude = Math.abs(rounded);
  if (magnitude === 0 || (magnitude >= 1e-3 && magnitude < 1e6)) {
    return String(rounded);
  }
  return rounded.toExponential().replace("e+", "e");
}
